import React, { useState } from 'react';
import { seedDatabase, clearSeedData } from '../utils/seedData';
import { userSearchService } from '../services/userSearchService';
import LinkingCodesDebug from '../components/debug/LinkingCodesDebug';

const TestDataPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [message, setMessage] = useState('');

  const handleSeedData = async () => {
    setLoading(true);
    setMessage('جاري إدخال البيانات التجريبية...');

    try {
      await seedDatabase();
      setMessage('✅ تم إدخال البيانات التجريبية بنجاح!');
    } catch (error) {
      setMessage('❌ خطأ في إدخال البيانات');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearData = async () => {
    setLoading(true);
    setMessage('جاري مسح البيانات التجريبية...');

    try {
      await clearSeedData();
      setMessage('✅ تم مسح البيانات التجريبية بنجاح!');
    } catch (error) {
      setMessage('❌ خطأ في مسح البيانات');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const results = await userSearchService.searchUsers(searchQuery, {
        role: 'parent',
        isActive: true,
      });

      setSearchResults(results.users);
      setMessage(`تم العثور على ${results.users.length} نتيجة`);
    } catch (error) {
      setMessage('❌ خطأ في البحث');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen bg-gray-50 py-8'>
      <div className='max-w-4xl mx-auto px-4'>
        <div className='bg-white rounded-lg shadow-md p-6'>
          <h1 className='text-2xl font-bold text-gray-900 mb-6 text-right'>
            إدارة البيانات الحقيقية والبحث
          </h1>

          {/* رسالة الحالة */}
          {message && (
            <div
              className={`mb-4 p-4 rounded-lg text-right ${
                message.includes('✅')
                  ? 'bg-green-100 text-green-800'
                  : message.includes('❌')
                  ? 'bg-red-100 text-red-800'
                  : 'bg-blue-100 text-blue-800'
              }`}
            >
              {message}
            </div>
          )}

          {/* أزرار إدارة البيانات */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-8'>
            <button
              onClick={handleSeedData}
              disabled={loading}
              className='bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-3 px-6 rounded-lg transition-colors'
            >
              {loading ? 'جاري المعالجة...' : 'إدخال البيانات التجريبية'}
            </button>

            <button
              onClick={handleClearData}
              disabled={loading}
              className='bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-medium py-3 px-6 rounded-lg transition-colors'
            >
              {loading ? 'جاري المعالجة...' : 'مسح البيانات التجريبية'}
            </button>
          </div>

          {/* قسم البحث */}
          <div className='border-t pt-6'>
            <h2 className='text-lg font-semibold text-gray-800 mb-4 text-right'>
              اختبار البحث عن أولياء الأمور
            </h2>

            <div className='flex gap-4 mb-4'>
              <input
                type='text'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder='ابحث عن ولي أمر (أحمد، فاطمة، محمد...)'
                className='flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right'
              />
              <button
                onClick={handleSearch}
                disabled={loading || !searchQuery.trim()}
                className='bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors'
              >
                بحث
              </button>
            </div>

            {/* نتائج البحث */}
            {searchResults.length > 0 && (
              <div className='mt-6'>
                <h3 className='font-medium text-gray-800 mb-3 text-right'>
                  النتائج ({searchResults.length}):
                </h3>
                <div className='space-y-3'>
                  {searchResults.map(parent => (
                    <div
                      key={parent.id}
                      className='bg-gray-50 p-4 rounded-lg border'
                    >
                      <div className='flex justify-between items-start'>
                        <div className='text-right'>
                          <h4 className='font-medium text-gray-900'>
                            {parent.name}
                          </h4>
                          <p className='text-sm text-gray-600'>
                            {parent.email}
                          </p>
                          <p className='text-sm text-gray-600'>
                            {parent.phone}
                          </p>
                          <p className='text-xs text-blue-600 mt-1'>
                            الدور: {parent.role} | الحالة:{' '}
                            {parent.isActive ? 'نشط' : 'غير نشط'}
                          </p>
                        </div>
                        <div className='bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded'>
                          {parent.id}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* رسالة عدم وجود نتائج */}
            {searchQuery && searchResults.length === 0 && !loading && (
              <div className='mt-6 text-center py-8 text-gray-500'>
                <p>لم يتم العثور على نتائج للبحث "{searchQuery}"</p>
                <p className='text-sm mt-2'>
                  تأكد من إدخال البيانات التجريبية أولاً
                </p>
              </div>
            )}
          </div>

          {/* معلومات البيانات التجريبية */}
          <div className='mt-8 bg-blue-50 p-4 rounded-lg'>
            <h3 className='font-medium text-blue-800 mb-2 text-right'>
              البيانات المتاحة للبحث:
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <h4 className='font-medium text-blue-900 mb-1'>
                  أولياء الأمور:
                </h4>
                <ul className='text-sm text-blue-700 space-y-1 text-right'>
                  <li>• أحمد محمد العلي</li>
                  <li>• فاطمة أحمد السالم</li>
                  <li>• محمد عبدالله الأحمد</li>
                  <li>• سارة علي المطيري</li>
                  <li>• خالد سعد العتيبي</li>
                  <li>• منى حسن الزهراني</li>
                  <li>• عبدالعزيز راشد القحطاني</li>
                  <li>• نورا عبدالرحمن الحربي</li>
                  <li>• ماجد فهد الدوسري</li>
                  <li>• ريم إبراهيم الشمري</li>
                </ul>
              </div>
              <div>
                <h4 className='font-medium text-blue-900 mb-1'>الطلاب:</h4>
                <ul className='text-sm text-blue-700 space-y-1 text-right'>
                  <li>• عبدالرحمن أحمد العلي</li>
                  <li>• نور فاطمة السالم</li>
                  <li>• عمر محمد الأحمد</li>
                  <li>• لينا سارة المطيري</li>
                  <li>• يوسف خالد العتيبي</li>
                </ul>
                <h4 className='font-medium text-blue-900 mb-1 mt-3'>
                  المدرسين:
                </h4>
                <ul className='text-sm text-blue-700 space-y-1 text-right'>
                  <li>• د. عمر الحسن المالكي</li>
                  <li>• أ. فاطمة الزهراء النجار</li>
                  <li>• م. أحمد عبدالله الشهري</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* تحليل أكواد الربط */}
        <div className='mt-8'>
          <LinkingCodesDebug />
        </div>
      </div>
    </div>
  );
};

export default TestDataPage;
